import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "./Icon";
import { Badge, IconButton, Kbd, LiveDot, Modal, Segmented, Tabs, cx } from "./ui";
import { useNow } from "../lib/hooks";
import { TZ } from "../lib/format";
import { TABS, activeTab, useConsole } from "../lib/console";

/** Sub-navigation of a page, bound to the #/page/tab route. */
export function PageTabs({ page, counts = {}, icons = {} }) {
  const { tab, navigate } = useConsole();
  return (
    <Tabs
      className="page-tabs"
      label="Page sections"
      value={activeTab(page, tab)}
      onChange={(id) => navigate(page, id)}
      tabs={(TABS[page] || []).map(([id, label]) => ({ id, label, count: counts[id], icon: icons[id] }))}
    />
  );
}

// ── Brand ───────────────────────────────────────────────────────
export function Logo({ size = 30, className }) {
  return (
    <svg className={cx("logo-mark", className)} width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="7" style={{ fill: "var(--brand)" }} />
      <path
        d="M16 5.5 8 8.6v6.6c0 5.6 3.5 9.4 8 11.3 4.5-1.9 8-5.7 8-11.3V8.6z"
        fill="none"
        style={{ stroke: "var(--brand-ink)" }}
        strokeWidth="1.9"
        strokeLinejoin="round"
      />
      <path
        d="m12.5 15.5 2.4 2.4 4.8-5"
        fill="none"
        style={{ stroke: "var(--brand-ink)" }}
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Wordmark({ sub = "Security operations" }) {
  return (
    <span className="logo-word">
      <strong>CyberShield</strong>
      <span>{sub}</span>
    </span>
  );
}

// ── Sidebar ─────────────────────────────────────────────────────
export function Sidebar({ groups, links = [], page, onNavigate, counts = {}, connected, simulated, rail, onToggleRail }) {
  return (
    <aside className="sidebar" aria-label="Primary">
      <div className="sb-brand">
        <Logo />
        <Wordmark />
      </div>
      <nav className="sb-nav">
        <div className="sb-workspace"><span className="sb-workspace-icon"><Icon name="layers" size={17} /></span><span className="sb-workspace-label"><strong>Security workspace</strong><small>CyberShield platform</small></span></div>
        {groups.map((g) => (
          <div className="sb-group" key={g.label}>
            <div className="sb-group-label">{g.label}</div>
            {g.items.map((item) => {
              const c = counts[item.id];
              const active = page === item.id;
              return (
                <a
                  key={item.id}
                  href={`#/${item.id}`}
                  className={cx("sb-link", active && "is-active")}
                  aria-current={active ? "page" : undefined}
                  title={rail ? item.label : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate(item.id);
                  }}
                >
                  <Icon name={item.icon} size={18} />
                  <span className="sb-link-label">{item.label}</span>
                  {c?.value > 0 && (
                    <>
                      <span className={cx("sb-count", c.tone && `tone-${c.tone}`)}>{c.value > 99 ? "99+" : c.value}</span>
                      <i className={cx("sb-dot", c.tone && `tone-${c.tone}`)} />
                    </>
                  )}
                </a>
              );
            })}
          </div>
        ))}
        {links.length > 0 && (
          <details className="sb-group sb-demo">
            <summary className="sb-group-label">Testing environment <Icon name="chevronDown" size={12} /></summary>
            {links.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="sb-link" title={l.hint}>
                <Icon name={l.icon} size={18} />
                <span className="sb-link-label">{l.label}</span>
                <Icon name="external" size={13} className="sb-ext" />
              </a>
            ))}
          </details>
        )}
      </nav>
      <div className="sb-foot">
        <div className="sb-status" title={connected ? "Live stream connected" : "Live stream reconnecting"}>
          <LiveDot tone={connected ? "ok" : "medium"} pulse={connected} />
          <span className="sb-status-text">
            <strong>{connected ? "Stream connected" : "Connecting to stream"}</strong>
            {simulated ? "Simulation enabled" : "Simulation paused"}
          </span>
        </div>
        <button type="button" className="sb-link sb-collapse" onClick={onToggleRail} aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}>
          <Icon name={rail ? "chevronRight" : "chevronLeft"} size={18} />
          <span className="sb-link-label">Collapse</span>
        </button>
      </div>
    </aside>
  );
}

// ── Top bar ─────────────────────────────────────────────────────
function Clock() {
  const now = useNow(1000);
  const t = new Date(now).toLocaleTimeString("en-GB", { timeZone: TZ, hour12: false });
  return (
    <span className="tb-clock" aria-label={`Gulf Standard Time ${t}`}>
      {t}
      <span>GST</span>
    </span>
  );
}

function UserMenu({ onSettings, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="avatar" aria-haspopup="menu" aria-expanded={open} aria-label="Account menu" onClick={() => setOpen((v) => !v)}>
        SOC
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-head">
            <strong>SOC analyst</strong>
            <span>Signed in to this console</span>
          </div>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => {
              setOpen(false);
              onSettings();
            }}
          >
            <Icon name="settings" size={16} /> Settings
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item is-danger"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <Icon name="logout" size={16} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function TopBar({ onMenu, onSearch, connected, sim, onToggleSim, theme, onToggleTheme, onSettings, onLogout, pageLabel, onAlerts, alertCount = 0, mode, onMode }) {
  const mac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "");
  return <header className="topbar">
    <IconButton icon="menu" label="Open navigation" className="tb-menu" onClick={onMenu} />
    <div className="tb-breadcrumb" aria-label="Current location"><Icon name="layers" size={15} /><span>Workspace</span><Icon name="chevronRight" size={13} /><strong>{pageLabel || "Overview"}</strong></div>
    <div className="tb-spacer" />
    {onMode && <div className="tb-mode"><Segmented size="sm" label="Interface mode" value={mode} onChange={onMode} options={[{ id: "basic", label: "Basic" }, { id: "advanced", label: "Advanced" }]} /></div>}
    <button type="button" className="tb-search" onClick={onSearch} aria-label="Search and jump to"><Icon name="search" size={15} /><span>Search the workspace</span><Kbd>{mac ? "⌘ K" : "Ctrl K"}</Kbd></button>
    <div className="tb-group">
      <span className={cx("tb-pill", connected ? "tone-ok" : "tone-medium")} title={connected ? "Receiving live events" : "Connection lost, retrying"}><LiveDot tone={connected ? "ok" : "medium"} pulse={false} /><span className="tb-pill-label">{connected ? "Live" : "Reconnecting"}</span></span>
      {sim && <button type="button" className="tb-pill tb-simulation" onClick={onToggleSim} title={sim.paused ? "Resume simulated traffic" : "Pause simulated traffic"} aria-label={sim.paused ? "Resume simulated traffic" : "Pause simulated traffic"}><Icon name={sim.paused ? "play" : "pause"} size={12} /><span className="tb-pill-label">{sim.paused ? "Paused" : "Simulation"}</span></button>}
      <Clock />
      <IconButton icon={theme === "dark" ? "sun" : "moon"} label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"} onClick={onToggleTheme} />
      <span className="tb-notifications"><IconButton icon="bell" label={"Open alerts" + (alertCount ? ", " + alertCount + " need review" : "")} onClick={onAlerts} />{alertCount > 0 && <i />}</span>
      <UserMenu onSettings={onSettings} onLogout={onLogout} />
    </div>
  </header>;
}

export function PageHeader({ eyebrow, icon, title, description, actions }) {
  return (
    <div className="page-head">
      <div>
        {eyebrow && (
          <div className="page-eyebrow">
            {icon && <Icon name={icon} size={14} />}
            {eyebrow}
          </div>
        )}
        <h1 className="page-title">{title}</h1>
        {description && <p className="page-desc">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Banner({ tone = "medium", icon = "alert", title, children, action }) {
  return (
    <div className={cx("banner", `tone-${tone}`)} role="status">
      <Icon name={icon} size={17} />
      <p>
        {title && <strong>{title}</strong>}
        {children}
      </p>
      {action}
    </div>
  );
}

// ── Command palette ─────────────────────────────────────────────
export function CommandPalette({ open, onClose, commands, ipActions }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
    }
  }, [open]);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const extra = ipActions ? ipActions(q.trim()) : [];
    const base = needle
      ? commands.filter((c) => `${c.label} ${c.group} ${c.keywords || ""}`.toLowerCase().includes(needle))
      : commands;
    return [...extra, ...base].slice(0, 40);
  }, [q, commands, ipActions]);

  useEffect(() => setIdx(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  const run = (c) => {
    onClose();
    setTimeout(() => c.run(), 0);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && items[idx]) {
      e.preventDefault();
      run(items[idx]);
    }
  };

  let lastGroup = null;
  return (
    <Modal open={open} onClose={onClose} width={620} label="Command palette">
      <div className="palette">
        <div className="palette-input">
          <Icon name="search" size={18} />
          <input
            data-autofocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to a page, run an action, or paste an IP address"
            aria-label="Search commands"
            aria-controls="palette-list"
            aria-activedescendant={items[idx] ? `pal-${idx}` : undefined}
            spellCheck={false}
          />
          <Badge tone="neutral">Esc</Badge>
        </div>
        <div className="palette-list" id="palette-list" role="listbox" ref={listRef}>
          {items.length === 0 && <div className="empty empty-compact"><p>No matches for “{q}”.</p></div>}
          {items.map((c, i) => {
            const head = c.group !== lastGroup ? c.group : null;
            lastGroup = c.group;
            return (
              <React.Fragment key={`${c.group}-${c.label}`}>
                {head && <div className="palette-group">{head}</div>}
                <button
                  id={`pal-${i}`}
                  data-idx={i}
                  type="button"
                  role="option"
                  aria-selected={i === idx}
                  className={cx("palette-item", i === idx && "is-active")}
                  onMouseMove={() => setIdx(i)}
                  onClick={() => run(c)}
                >
                  <Icon name={c.icon || "arrowRight"} size={16} />
                  <span>{c.label}</span>
                  {c.hint && <small>{c.hint}</small>}
                </button>
              </React.Fragment>
            );
          })}
        </div>
        <div className="palette-foot">
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </span>
          <span>
            <Kbd>↵</Kbd> open
          </span>
          <span>
            <Kbd>esc</Kbd> close
          </span>
        </div>
      </div>
    </Modal>
  );
}
